import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronDown, Check } from 'lucide-react';
import { COUNTRY_CODES } from '@/lib/constants/countryCodes';
import { MAX_PHONE_DIGITS } from '@/lib/constants/phoneValidation';
import { cn } from '@/lib/utils';

function isoFromFlagEmoji(flag: string): string {
  try {
    const letters = Array.from(flag)
      .map((c) => c.codePointAt(0) || 0)
      .filter((cp) => cp >= 0x1f1e6 && cp <= 0x1f1ff)
      .map((cp) => String.fromCharCode(cp - 0x1f1e6 + 65));
    return letters.length === 2 ? letters.join('').toLowerCase() : '';
  } catch {
    return '';
  }
}

function Flag({ flag }: { flag: string }) {
  const iso = isoFromFlagEmoji(flag);
  const [failed, setFailed] = useState(false);
  if (!iso || failed) {
    return <span className="text-base leading-none">{flag}</span>;
  }
  return (
    <img
      src={`https://flagcdn.com/${iso}.svg`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-4 w-[22px] shrink-0 rounded-[2px] object-cover"
    />
  );
}

interface PhoneNumberInputProps {
  countryCode: string;
  phoneDisplay: string;
  onCountryChange: (code: string) => void;
  onPhoneChange: (digits: string) => void;
  invalid?: boolean;
  id?: string;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  onBlur?: () => void;
}

export function PhoneNumberInput({
  countryCode,
  phoneDisplay,
  onCountryChange,
  onPhoneChange,
  invalid = false,
  id,
  autoFocus,
  placeholder = 'Your phone number',
  className,
  onBlur,
}: PhoneNumberInputProps) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRY_CODES.find((c) => c.code === countryCode);

  return (
    <div
      className={cn(
        'flex h-10 w-full items-center rounded-lg border border-[#E4E7EC] bg-white transition',
        'focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/15',
        invalid && 'border-red-400 focus-within:border-red-400 focus-within:ring-red-400',
        className,
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label="Select country code"
            className="flex h-full shrink-0 items-center gap-1 rounded-l-lg pl-2.5 pr-2 text-[13px] text-[#101828] outline-none transition-colors hover:bg-[#F9FAFB] focus-visible:bg-[#F9FAFB]"
          >
            <Flag flag={selected?.flag || '🇮🇳'} />
            <span className="font-medium tabular-nums">{countryCode}</span>
            <ChevronDown className="h-3 w-3 shrink-0 text-[#98A2B3]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[300px] max-h-[400px] overflow-hidden rounded-xl border border-[#E4E7EC] bg-white p-0 shadow-[0_8px_20px_rgba(16,24,40,0.08)]"
          align="start"
        >
          <Command
            onWheel={(e) => e.stopPropagation()}
            className="rounded-xl bg-white [&_[cmdk-input-wrapper]]:border-[#E4E7EC]"
          >
            <CommandInput
              placeholder="Search country..."
              className="h-10 text-[13px] font-medium text-[#101828] placeholder:text-[#98A2B3]"
            />
            <CommandList className="max-h-[300px] overflow-y-auto p-1">
              <CommandEmpty className="py-4 text-center text-[13px] text-[#667085]">
                No country found.
              </CommandEmpty>
              <CommandGroup className="p-0">
                {COUNTRY_CODES.map((country) => (
                  <CommandItem
                    key={`${country.code}-${country.country}`}
                    value={`${country.country} ${country.code}`}
                    onSelect={() => {
                      onCountryChange(country.code);
                      setOpen(false);
                    }}
                    className="cursor-pointer rounded-lg px-2 py-1.5 text-[13px] font-medium text-[#101828] data-[selected='true']:bg-[#F9FAFB] data-[selected='true']:text-[#101828] data-[selected=true]:bg-[#F9FAFB] data-[selected=true]:text-[#101828]"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-3.5 w-3.5 shrink-0 text-[#6B7280]',
                        countryCode === country.code ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <Flag flag={country.flag} />
                    <span className="ml-2 flex-1">{country.country}</span>
                    <span className="text-[12px] font-normal text-[#667085]">{country.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <span className="h-5 w-px shrink-0 bg-[#E4E7EC]" aria-hidden="true" />

      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoFocus={autoFocus}
        maxLength={MAX_PHONE_DIGITS}
        placeholder={placeholder}
        aria-invalid={invalid}
        required
        value={phoneDisplay}
        onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS))}
        onBlur={onBlur}
        className="no-autofill-bg h-full min-w-0 flex-1 rounded-r-lg bg-transparent px-2.5 text-[13px] text-[#101828] outline-none placeholder:text-[#98A2B3] tabular-nums"
      />
    </div>
  );
}
